import { useCallback, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  BuildingRegular,
  EditRegular,
  AddRegular,
  CopyRegular,
  ContactCardRegular,
  ArrowClockwiseRegular,
} from '@fluentui/react-icons';
import DataTable, { type Column } from '../components/DataTable';
import { RowLink } from '../components/links/EntityLink';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { getTaxClients, updateXeroContactId } from '../services/api';
import {
  type TaxClientListItem,
  statusDisplayNames,
  statusColors,
} from '../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: tokens.spacingVerticalL,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  headerIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorBrandForeground1,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  statCard: {
    padding: tokens.spacingHorizontalL,
    textAlign: 'center',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: '600',
    lineHeight: '1.2',
  },
  statLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  tableCard: {
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalL,
  },
  clientNameCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  clientNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: '600',
  },
  clientSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginLeft: '20px',
  },
  taxYearBadges: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
  xeroCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  xeroIdText: {
    fontFamily: "Consolas, 'Courier New', Courier, monospace",
    fontSize: tokens.fontSizeBase200,
  },
  xeroNotSet: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  dialogWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
});

interface EditXeroDialogState {
  open: boolean;
  client: TaxClientListItem | null;
  value: string;
}

export default function TaxClientsPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [typeFilter, setTypeFilter] = useState<'all' | 'individual' | 'business'>('all');
  const [editDialog, setEditDialog] = useState<EditXeroDialogState>({
    open: false,
    client: null,
    value: '',
  });

  const { data: clients = [], isLoading, error, refetch } = useQuery({
    queryKey: ['taxClients'],
    queryFn: getTaxClients,
  });

  const updateMutation = useMutation({
    mutationFn: ({ clientId, xeroContactId }: { clientId: string; xeroContactId: string | null }) =>
      updateXeroContactId(clientId, xeroContactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxClients'] });
      setEditDialog({ open: false, client: null, value: '' });
    },
  });

  const filteredClients = useMemo(() => {
    if (typeFilter === 'all') return clients;
    return clients.filter(c => c.clientType === typeFilter);
  }, [clients, typeFilter]);

  const stats = useMemo(() => {
    const total = clients.length;
    const individuals = clients.filter(c => c.clientType === 'individual').length;
    const businesses = clients.filter(c => c.clientType === 'business').length;
    const withXero = clients.filter(c => c.xeroContactId).length;
    const withoutXero = total - withXero;
    return { total, individuals, businesses, withXero, withoutXero };
  }, [clients]);

  const openEditDialog = (client: TaxClientListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditDialog({ open: true, client, value: client.xeroContactId ?? '' });
  };

  const handleCopyXeroId = (xeroContactId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(xeroContactId).catch(() => {/* silent */});
  };

  const handleSaveXero = () => {
    if (!editDialog.client) return;
    const trimmed = editDialog.value.trim();
    updateMutation.mutate({
      clientId: editDialog.client.clientId,
      xeroContactId: trimmed === '' ? null : trimmed,
    });
  };

  // Real links (fleet ruling 2026-10-29): a client with no return on file has nowhere to go.
  const getTaxClientDetailHref = useCallback(
    (item: TaxClientListItem): string | undefined =>
      item.latestReturnId ? `/tax-returns/${item.latestReturnId}` : undefined,
    [],
  );

  const columns: Column<TaxClientListItem>[] = [
    {
      key: 'clientName',
      header: 'Client Name',
      sortable: true,
      width: '220px',
      getValue: (item) => item.clientName,
      render: (item) => {
        const href = getTaxClientDetailHref(item);
        return (
        <div className={styles.clientNameCell}>
          <div className={styles.clientNameRow}>
            {item.clientType === 'business'
              ? <BuildingRegular fontSize={14} />
              : <PersonRegular fontSize={14} />}
            {href ? <RowLink href={href}>{item.clientName}</RowLink> : item.clientName}
          </div>
          {item.scheduleCBusinessName && (
            <div className={styles.clientSubtitle}>
              {item.scheduleCBusinessName}
            </div>
          )}
        </div>
        );
      },
    },
    {
      key: 'clientType',
      header: 'Type',
      sortable: true,
      width: '110px',
      getValue: (item) => item.clientType,
      render: (item) => (
        <Badge
          appearance="tint"
          color={item.clientType === 'business' ? 'brand' : 'informative'}
          size="small"
        >
          {item.clientType === 'business' ? 'Business' : 'Individual'}
        </Badge>
      ),
    },
    {
      key: 'clientEmail',
      header: 'Email',
      sortable: true,
      width: '200px',
      getValue: (item) => item.clientEmail ?? '',
      render: (item) => (
        <Text size={200}>{item.clientEmail ?? '-'}</Text>
      ),
    },
    {
      key: 'clientPhone',
      header: 'Phone',
      sortable: false,
      width: '130px',
      render: (item) => (
        <Text size={200}>{item.clientPhone ?? '-'}</Text>
      ),
    },
    {
      key: 'taxYears',
      header: 'Tax Years',
      sortable: false,
      width: '140px',
      render: (item) => (
        <div className={styles.taxYearBadges}>
          {item.taxYears.length === 0
            ? <Text size={200}>-</Text>
            : item.taxYears
                .slice()
                .sort((a, b) => b - a)
                .map(year => (
                  <Badge key={year} appearance="outline" size="small">
                    {year}
                  </Badge>
                ))}
        </div>
      ),
    },
    {
      key: 'returnCount',
      header: 'Returns',
      sortable: true,
      width: '80px',
      getValue: (item) => item.returnCount,
      render: (item) => (
        <Text size={200}>{item.returnCount}</Text>
      ),
    },
    {
      key: 'latestStatus',
      header: 'Latest Status',
      sortable: true,
      width: '160px',
      getValue: (item) => item.latestStatus ?? '',
      render: (item) => {
        if (!item.latestStatus) return <Text size={200}>-</Text>;
        const color = statusColors[item.latestStatus as keyof typeof statusColors];
        const label = statusDisplayNames[item.latestStatus as keyof typeof statusDisplayNames] ?? item.latestStatus;
        return (
          <Badge
            appearance="filled"
            size="small"
            style={{ backgroundColor: color, color: '#fff' }}
          >
            {label}
          </Badge>
        );
      },
    },
    {
      key: 'xeroContactId',
      header: 'Xero Contact ID',
      sortable: true,
      width: '200px',
      getValue: (item) => item.xeroContactId ?? '',
      render: (item) => (
        <div className={styles.xeroCell}>
          {item.xeroContactId ? (
            <>
              <Tooltip content={item.xeroContactId} relationship="label">
                <Text className={styles.xeroIdText} truncate>
                  {item.xeroContactId.length > 16
                    ? `${item.xeroContactId.substring(0, 8)}...`
                    : item.xeroContactId}
                </Text>
              </Tooltip>
              <Tooltip content="Copy Xero Contact ID" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<CopyRegular />}
                  size="small"
                  onClick={(e) => handleCopyXeroId(item.xeroContactId!, e)}
                />
              </Tooltip>
              <Tooltip content="Edit Xero Contact ID" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<EditRegular />}
                  size="small"
                  onClick={(e) => openEditDialog(item, e)}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <Text size={200} className={styles.xeroNotSet}>Not set</Text>
              <Tooltip content="Add Xero Contact ID" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<AddRegular />}
                  size="small"
                  onClick={(e) => openEditDialog(item, e)}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <TableSkeleton rows={8} columns={8} />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Failed to load tax clients. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerRow}>
          <ContactCardRegular className={styles.headerIcon} />
          <h2 style={{ margin: 0 }}>Tax Clients</h2>
        </div>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
          All clients with tax returns in the system
        </Text>
      </div>

      {/* Stats Bar */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: tokens.colorBrandForeground1 }}>
            {stats.total}
          </div>
          <div className={styles.statLabel}>Total Clients</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#3B82F6' }}>
            {stats.individuals}
          </div>
          <div className={styles.statLabel}>Individuals</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#8B5CF6' }}>
            {stats.businesses}
          </div>
          <div className={styles.statLabel}>Businesses</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#10B981' }}>
            {stats.withXero}
          </div>
          <div className={styles.statLabel}>With Xero ID</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#F59E0B' }}>
            {stats.withoutXero}
          </div>
          <div className={styles.statLabel}>Without Xero ID</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Dropdown
          value={typeFilter === 'all' ? 'All Types' : typeFilter === 'individual' ? 'Individual' : 'Business'}
          onOptionSelect={(_, d) => setTypeFilter(d.optionValue as 'all' | 'individual' | 'business')}
          style={{ minWidth: '140px' }}
        >
          <Option value="all" text="All Types">All Types</Option>
          <Option value="individual" text="Individual">Individual</Option>
          <Option value="business" text="Business">Business</Option>
        </Dropdown>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={() => refetch()}
          style={{ marginLeft: 'auto' }}
        >
          Refresh
        </Button>
      </div>

      {/* Data Table */}
      <div className={styles.tableCard}>
        <DataTable
          data={filteredClients}
          columns={columns}
          rowKey="clientId"
          searchable
          searchPlaceholder="Search by client name, email..."
          searchKeys={['clientName', 'clientEmail', 'clientPhone']}
          paginated
          initialSort={{ column: 'clientName', direction: 'asc' }}
          emptyMessage="No clients found."
          getRowHref={getTaxClientDetailHref}
        />
      </div>

      {/* Edit Xero Contact ID Dialog */}
      <Dialog
        open={editDialog.open}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setEditDialog({ open: false, client: null, value: '' });
          }
        }}
      >
        <DialogSurface style={{ maxWidth: '420px' }}>
          <DialogBody>
            <DialogTitle>
              {editDialog.client?.xeroContactId ? 'Edit Xero Contact ID' : 'Add Xero Contact ID'}
            </DialogTitle>
            <DialogContent>
              {editDialog.client && (
                <>
                  <Text size={300} style={{ display: 'block', marginBottom: tokens.spacingVerticalM }}>
                    Client: <strong>{editDialog.client.clientName}</strong>
                  </Text>
                  <Field label="Xero Contact ID">
                    <Input
                      value={editDialog.value}
                      onChange={(_, data) => setEditDialog(prev => ({ ...prev, value: data.value }))}
                      placeholder="Enter Xero Contact ID..."
                      style={{ width: '100%' }}
                    />
                  </Field>
                  <div className={styles.dialogWarning}>
                    This will update the local Xero Contact ID. It will NOT sync to Dataverse.
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {editDialog.client?.xeroContactId && (
                <Button
                  appearance="subtle"
                  onClick={() => {
                    if (!editDialog.client) return;
                    updateMutation.mutate({ clientId: editDialog.client.clientId, xeroContactId: null });
                  }}
                  disabled={updateMutation.isPending}
                >
                  Clear
                </Button>
              )}
              <Button
                appearance="secondary"
                onClick={() => setEditDialog({ open: false, client: null, value: '' })}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSaveXero}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
